/**
 * Error Analytics — vibrant analytics dashboard for application error logs.
 * Light theme. Charts are hand-built SVG/CSS (no chart lib → zero extra deps).
 *
 * LIVE DATA (same backend the Error-Logs Monitor uses):
 *   errorLogsService.getDashboardData() → KPIs, severity split, sources,
 *                                          hourly trends, top error groups
 *   errorLogsService.search()           → the raw error-log records
 * Falls back to sample data only when the API returns nothing.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { errorLogsService } from '../../services/error-logs.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { Calendar } from 'sharedUi/Calendar';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import type { ErrorGroup, ErrorLog, ErrorLogDashboardStats } from '../../types';
import type { TimeRange } from '../../types/error-logs.types';
import './error-analytics.css';
import SearchInput from '../../components/common/search-input';
import ProjectFilter from '../../components/common/project-filter';

/* ------------------------------------------------------------------ */
/* palette + helpers                                                   */
/* ------------------------------------------------------------------ */
const C = {
  blue: '#3B82F6', indigo: '#6366F1', violet: '#8B5CF6', purple: '#A855F7',
  cyan: '#06B6D4', teal: '#14B8A6', green: '#22C55E', amber: '#F59E0B',
  orange: '#FB923C', red: '#EF4444', pink: '#EC4899', sky: '#0EA5E9',
};
type Sev = 'Critical' | 'High' | 'Medium' | 'Low';
// HIGH is red; CRITICAL is a deeper red so the two remain distinguishable at a glance.
// Keep in sync with --ea-critical / --ea-high in error-analytics.css.
const SEVC: Record<Sev, string> = { Critical: '#B91C1C', High: '#EF4444', Medium: '#EAB308', Low: '#22C55E' };
const SEV_ORDER: Sev[] = ['Critical', 'High', 'Medium', 'Low'];
const toSev = (up?: string): Sev => (({ CRITICAL: 'Critical', HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' } as Record<string, Sev>)[(up || '').toUpperCase()] ?? 'Low');
const SRC: Record<string, string> = { API: C.indigo, UI: C.pink, SCREEN: C.cyan, THIRDPARTY: C.amber, FUNCTION: C.green, SERVICE: C.teal, DATABASE: C.blue, GATEWAY: C.orange, WORKER: C.sky, BACKEND: C.violet, CONTROLLER: C.purple };
const srcColor = (n: string) => SRC[n.toUpperCase()] ?? C.sky;

const fmt = (n: number) => Math.round(n || 0).toLocaleString('en-US');
/** Compact form for labels drawn inside a narrow chart column (1263 → 1.3k). */
const fmtShort = (n: number) => {
  const v = Math.round(n || 0);
  if (v < 1000) return String(v);
  return v >= 10000 ? `${Math.round(v / 1000)}k` : `${(v / 1000).toFixed(1)}k`;
};
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const makeRng = (seed: number) => { let s = seed % 0x7fffffff; return () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; }; };
const fmtTime = (d: Date) => d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' });
function timeAgo(iso?: string): string {
  if (!iso) return '—'; const t = new Date(iso).getTime(); if (Number.isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000); if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`;
}

/** Horizontal range picker inside the record drawer's Timeline tab. */
const TL_RANGES: { label: string; value: TimeRange; short: string }[] = [
  { label: '1 Day', value: '24h', short: 'last 1 day' },
  { label: '7 Days', value: '7d', short: 'last 7 days' },
  { label: '30 Days', value: '30d', short: 'last 30 days' },
  { label: 'All time', value: 'all', short: 'all time' },
];

type TipContent = ReactNode;
interface Tip { show: (c: TipContent, x: number, y: number) => void; hide: () => void; }

interface KpiVM {
  key: string; label: string; val: number; unit?: string; color: string; tip: string;
  /** Source-type split rendered beside the count (Total / Critical / High cards). */
  sources?: { name: string; v: number }[];
  /** Turns the card into a "drill in" affordance with an arrow (Unique / Users cards). */
  drill?: { text: string; open: () => void };
}
interface Part { name: string; v: number; c: string; }
interface TLPoint { label: string; total: number; parts: Part[]; }
interface DonutItem { name: string; v: number; c: string; }
interface CodeVM {
  code: string; desc: string; v: number; sev: Sev; svc: string[]; last: string; extra: string; users: number;
  type?: string; endpoint?: string; params?: Record<string, unknown>; firstSeen?: string; lastSeen?: string; raw?: ErrorGroup;
  /** Set only for a single log record — a group has none of these. */
  isRecord?: boolean; userId?: string; userName?: string; env?: string; method?: string; ip?: string;
}
interface EndpointVM { name: string; v: number; sev: Sev; codes: number; last: string; }
interface UserVM { user: string; userId: string; v: number; }
const initials = (n: string) => (n.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();

/** `parameters` is stored either as an object or as a list of {name, value} pairs. */
/** Raw user identifiers are never surfaced, so payload keys holding one are hidden. */
const USER_ID_KEY = /(^|_)(actor)?user_?id$|^actor_?id$|^(created|updated|deleted)_?by$/i;

/** Parameters are stored either as an object or as a [{name, value}] list. */
function normaliseParams(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, unknown> = {};
  if (Array.isArray(raw)) {
    (raw as { name?: string; value?: unknown }[]).forEach((p) => {
      if (p && typeof p === 'object' && p.name && !USER_ID_KEY.test(String(p.name))) {
        out[String(p.name)] = p.value;
      }
    });
  } else {
    Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
      if (!USER_ID_KEY.test(k)) out[k] = v;
    });
  }
  return Object.keys(out).length ? out : undefined;
}
interface TableVM { ts: Date; source: string; type: string; sev: Sev; code: string; env: string; user: string; ip: string; msg: string; raw?: ErrorLog; }

/* ------------------------------------------------------------------ */
/* building blocks                                                     */
/* ------------------------------------------------------------------ */
function CountUp({ value, unit }: { value: number; unit?: string }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0; const t0 = performance.now(); const dur = 850;
    const step = (now: number) => { const p = Math.min((now - t0) / dur, 1); setV(value * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, [value]);
  const d = value % 1 !== 0 ? 1 : 0;
  return <>{d ? v.toFixed(d) : Math.round(v).toLocaleString('en-US')}{unit ? <small>{unit}</small> : null}</>;
}
function SevChip({ s }: { s: Sev }) { return <span className={`ea-sev ${s.toLowerCase()}`}>{s}</span>; }

/* ------------------------------------------------------------------ */
/* fallback sample data                                                */
/* ------------------------------------------------------------------ */
const SAMPLE_CODES: CodeVM[] = [
  { code: 'ERR.PAY.GATEWAY.TIMEOUT', desc: 'Upstream payment gateway did not respond within 3000ms', v: 8412, sev: 'Critical', svc: ['payment-svc'], last: '2m ago', extra: '1,204 users', users: 1204 },
  { code: 'ERR.AUTH.TOKEN.EXPIRED', desc: 'JWT access token expired before refresh completed', v: 6120, sev: 'High', svc: ['auth-core'], last: 'just now', extra: '980 users', users: 980 },
  { code: 'ERR.DB.POOL.EXHAUSTED', desc: 'Mongo connection pool exhausted — max 200 reached', v: 4890, sev: 'Critical', svc: ['payment-svc'], last: '1m ago', extra: '640 users', users: 640 },
  { code: 'ERR.API.RATE_LIMIT', desc: 'Downstream partner API returned 429 Too Many Requests', v: 3760, sev: 'High', svc: ['search-gw'], last: '5m ago', extra: '410 users', users: 410 },
  { code: 'ERR.NET.DNS.RESOLVE', desc: 'DNS resolution failed for internal service mesh host', v: 2540, sev: 'Medium', svc: ['notify-wkr'], last: '3m ago', extra: '220 users', users: 220 },
  { code: 'ERR.MEM.OOM.KILLED', desc: 'Container OOM-killed — heap exceeded 2Gi limit', v: 1980, sev: 'Critical', svc: ['reporting'], last: '8m ago', extra: '90 users', users: 90 },
  { code: 'ERR.VALIDATION.SCHEMA', desc: 'Request body failed schema validation at /orders', v: 1720, sev: 'Low', svc: ['checkout-fe'], last: '6m ago', extra: '150 users', users: 150 },
  { code: 'ERR.THIRDPARTY.STRIPE', desc: 'Stripe webhook signature verification failed', v: 1340, sev: 'Medium', svc: ['payment-svc'], last: '12m ago', extra: '70 users', users: 70 },
  { code: 'ERR.CPU.THROTTLE', desc: 'Sustained CPU throttling on node pool workers-3', v: 980, sev: 'Medium', svc: ['notify-wkr'], last: '15m ago', extra: '40 users', users: 40 },
  { code: 'ERR.STORAGE.WRITE', desc: 'S3 multipart upload aborted — connection reset', v: 640, sev: 'Low', svc: ['reporting'], last: '20m ago', extra: '30 users', users: 30 },
];
const SAMPLE_ENDPOINTS: EndpointVM[] = [
  { name: 'POST /api/v1/checkout/submit', v: 8412, sev: 'Critical', codes: 3, last: '2m ago' },
  { name: 'POST /api/v1/auth/token', v: 6120, sev: 'High', codes: 2, last: 'just now' },
  { name: 'POST /api/v1/payments/charge', v: 4890, sev: 'Critical', codes: 4, last: '1m ago' },
  { name: 'GET /api/v1/search', v: 3760, sev: 'High', codes: 2, last: '5m ago' },
  { name: 'GET /api/v1/inventory', v: 2540, sev: 'Medium', codes: 1, last: '3m ago' },
  { name: 'POST /api/v1/reports/export', v: 1980, sev: 'Critical', codes: 2, last: '8m ago' },
  { name: 'POST /api/v1/orders', v: 1720, sev: 'Low', codes: 1, last: '6m ago' },
  { name: 'POST /webhooks/stripe', v: 1340, sev: 'Medium', codes: 1, last: '12m ago' },
  { name: 'GET /api/v1/notifications', v: 980, sev: 'Medium', codes: 1, last: '15m ago' },
  { name: 'PUT /api/v1/files', v: 640, sev: 'Low', codes: 1, last: '20m ago' },
];
const SAMPLE_USERS: UserVM[] = [
  { user: 'System Administrator', userId: 'sys_admin', v: 120 }, { user: 'Manasa Vemireddy', userId: 'u_manasa', v: 6 },
  { user: 'Ravi Kumar', userId: 'u_ravi', v: 5 }, { user: 'Ava Chen', userId: 'u_ava', v: 3 },
  { user: 'Sam Okafor', userId: 'u_sam', v: 2 }, { user: 'Lena Roth', userId: 'u_lena', v: 1 },
];
const SAMPLE_DIST: DonutItem[] = [
  { name: 'Critical', v: 1284, c: SEVC.Critical }, { name: 'High', v: 6820, c: SEVC.High },
  { name: 'Medium', v: 12440, c: SEVC.Medium }, { name: 'Low', v: 28165, c: SEVC.Low },
];
const SAMPLE_SRC: DonutItem[] = [
  { name: 'API', v: 16800, c: SRC.API }, { name: 'SCREEN', v: 11200, c: SRC.SCREEN },
  { name: 'FUNCTION', v: 8400, c: SRC.FUNCTION }, { name: 'THIRDPARTY', v: 5200, c: SRC.THIRDPARTY }, { name: 'UI', v: 3400, c: SRC.UI },
];
function sampleTrend(): TLPoint[] {
  const rng = makeRng(731);
  return Array.from({ length: 24 }, (_, h) => {
    const wave = 1 + 0.6 * Math.sin((h / 24) * Math.PI * 2 - 0.6);
    const total = Math.max(4, Math.round(80 * wave * (0.75 + rng() * 0.5)));
    const parts: Part[] = [
      { name: 'API', v: Math.round(total * 0.4), c: SRC.API }, { name: 'SCREEN', v: Math.round(total * 0.3), c: SRC.SCREEN },
      { name: 'UI', v: Math.round(total * 0.18), c: SRC.UI }, { name: 'THIRDPARTY', v: Math.round(total * 0.12), c: SRC.THIRDPARTY },
    ];
    return { label: `${String(h).padStart(2, '0')}:00`, total, parts };
  });
}
function sampleRows(seed: number): TableVM[] {
  const rng = makeRng(seed); const now = Date.now(); const envs = ['production', 'staging'];
  const users = ['u_10428', 'u_20913', 'u_33871', 'u_50142', '—'];
  return Array.from({ length: 40 }, (_, i) => {
    const c = SAMPLE_CODES[Math.floor(rng() * SAMPLE_CODES.length)];
    return { ts: new Date(now - i * 137000 - Math.floor(rng() * 90000)), source: c.svc[0], type: ['API', 'SCREEN', 'UI', 'FUNCTION', 'THIRDPARTY'][Math.floor(rng() * 5)], sev: c.sev, code: c.code, env: envs[Math.floor(rng() * 2)], user: users[Math.floor(rng() * users.length)], ip: `10.0.${Math.floor(rng() * 9)}.${Math.floor(rng() * 200)}`, msg: c.desc };
  });
}

/* ------------------------------------------------------------------ */
/* charts                                                              */
/* ------------------------------------------------------------------ */
/**
 * Monotone cubic interpolation (Fritsch–Carlson).
 *
 * The previous Catmull-Rom smoothing let control points overshoot: a flat stretch
 * followed by a sharp climb pushed the curve *below* the baseline, so a series that
 * is 0 for days appeared to go negative. Monotone tangents cannot overshoot the
 * data points, so a run of zeros stays flat at zero.
 */
function smoothPath(pts: number[][]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n < 3) return pts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ');

  const dx: number[] = [], slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    slope[i] = dx[i] === 0 ? 0 : (pts[i + 1][1] - pts[i][1]) / dx[i];
  }

  const t: number[] = new Array(n);
  t[0] = slope[0];
  t[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) {
      t[i] = 0;                       // local extremum → flat tangent, no overshoot
    } else {
      const w1 = 2 * dx[i] + dx[i - 1], w2 = dx[i] + 2 * dx[i - 1];
      t[i] = (w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]);
    }
  }

  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < n - 1; i++) {
    const x1 = pts[i][0] + dx[i] / 3, y1 = pts[i][1] + (t[i] * dx[i]) / 3;
    const x2 = pts[i + 1][0] - dx[i] / 3, y2 = pts[i + 1][1] - (t[i + 1] * dx[i]) / 3;
    d += ` C${x1.toFixed(1)} ${y1.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)} ${pts[i + 1][0].toFixed(1)} ${pts[i + 1][1].toFixed(1)}`;
  }
  return d;
}
/** One plotted line: a severity level or a source type across the timeline buckets. */
interface TLSeries { name: string; color: string; values: number[]; group: 'severity' | 'source' }

function Timeline({ points, series, tip }: { points: TLPoint[]; series: TLSeries[]; tip: Tip }) {
  const [hover, setHover] = useState<number | null>(null);
  const data = points.length ? points : [{ label: '—', total: 0, parts: [] }];
  const W = 900, H = 280, L = 46, Rp = 16, T = 16, B = 30;
  const iw = W - L - Rp, ih = H - T - B;
  // Scale to the tallest thing drawn — the total line, or a series if none is drawn
  const peak = Math.max(...data.map((d) => d.total), ...series.flatMap((s) => s.values), 0);
  const mx = (peak || 1) * 1.18;
  const xOf = (i: number) => L + (data.length === 1 ? iw / 2 : (i * iw) / (data.length - 1));
  const yOf = (v: number) => T + ih - (v / mx) * ih;
  const pts = data.map((d, i) => [xOf(i), yOf(d.total)]);
  const linePath = smoothPath(pts);
  const baseY = T + ih;
  const areaPath = `${linePath} L${xOf(data.length - 1).toFixed(1)} ${baseY.toFixed(1)} L${xOf(0).toFixed(1)} ${baseY.toFixed(1)} Z`;
  const showDots = data.length <= 14;
  const overlayRef = useRef<SVGRectElement>(null);
  const onMove = (e: React.MouseEvent) => {
    const r = overlayRef.current!.getBoundingClientRect();
    const idx = clamp(Math.round(((e.clientX - r.left) / r.width) * (data.length - 1)), 0, data.length - 1);
    setHover(idx); const d = data[idx];
    tip.show(
      <>
        <div className="t">{d.label}</div>
        <div className="r"><span><span className="ea-sw" style={{ background: C.indigo }} />Total</span><b>{fmt(d.total)}</b></div>
        {series.filter((s) => (s.values[idx] ?? 0) >= 0).map((s) => (
          <div className="r" key={`${s.group}-${s.name}`}>
            <span><span className="ea-sw" style={{ background: s.color }} />{s.name}</span><b>{fmt(s.values[idx])}</b>
          </div>
        ))}
      </>, e.clientX, e.clientY);
  };
  const yl = 4, ticks = Math.max(1, Math.round(data.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="eaArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={C.indigo} stopOpacity="0.32" /><stop offset="0.6" stopColor={C.violet} stopOpacity="0.1" /><stop offset="1" stopColor={C.violet} stopOpacity="0" /></linearGradient>
        <linearGradient id="eaStroke" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={C.cyan} /><stop offset="0.5" stopColor={C.indigo} /><stop offset="1" stopColor={C.violet} /></linearGradient>
      </defs>
      {Array.from({ length: yl + 1 }, (_, k) => { const v = (mx * k) / yl, y = yOf(v); return <g key={k}><line className="ea-gl" x1={L} y1={y} x2={W - Rp} y2={y} /><text className="ea-axis" x={L - 8} y={y + 3.5} textAnchor="end">{v >= 1000 ? (v / 1000).toFixed(1) + 'k' : Math.round(v)}</text></g>; })}
      {/* the total is only drawn when there is no breakdown to show */}
      {!series.length && <path d={areaPath} fill="url(#eaArea)" />}
      {!series.length && <path d={linePath} fill="none" stroke="url(#eaStroke)" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />}
      {/* plain solid lines — one per level or source type */}
      {series.map((s) => (
        <path key={`${s.group}-${s.name}`} d={smoothPath(s.values.map((v, i) => [xOf(i), yOf(v)]))}
          fill="none" stroke={s.color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {showDots && !series.length && pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#fff" stroke={C.indigo} strokeWidth="2" />)}
      {hover !== null && (
        <g>
          <line className="ea-cross" x1={xOf(hover)} y1={T} x2={xOf(hover)} y2={T + ih} />
          {series.map((s) => <circle key={`h-${s.group}-${s.name}`} cx={xOf(hover)} cy={yOf(s.values[hover] ?? 0)} r="4" fill={s.color} stroke="#fff" strokeWidth="2" />)}
          <circle cx={xOf(hover)} cy={yOf(data[hover].total)} r="5" fill={C.indigo} stroke="#fff" strokeWidth="2.5" />
        </g>
      )}
      {data.map((d, i) => (i % ticks === 0 || i === data.length - 1) ? <text key={i} className="ea-axis" x={xOf(i)} y={H - 10} textAnchor="middle">{d.label}</text> : null)}
      <rect ref={overlayRef} x={L} y={T} width={iw} height={ih} fill="transparent" onMouseMove={onMove} onMouseLeave={() => { setHover(null); tip.hide(); }} style={{ cursor: 'crosshair' }} />
    </svg>
  );
}
function Donut({ items, size = 152, tip, onPick }: { items: DonutItem[]; size?: number; tip: Tip; onPick?: (name: string) => void }) {
  const total = items.reduce((a, b) => a + b.v, 0) || 1;
  const r = size / 2, rad = size * 0.36, sw = size * 0.15, Circ = 2 * Math.PI * rad, gap = 2;
  const [hover, setHover] = useState<number | null>(null);
  let off = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ display: 'block' }}>
      {items.map((d, i) => {
        const len = Circ * (d.v / total); const dash = `${Math.max(0, len - gap)} ${Circ - Math.max(0, len - gap)}`; const el = (
          <circle key={i} cx={r} cy={r} r={rad} fill="none" stroke={d.c} strokeWidth={hover === i ? sw * 1.28 : sw} strokeDasharray={dash} strokeDashoffset={-off} strokeLinecap="round" transform={`rotate(-90 ${r} ${r})`} style={{ cursor: 'pointer', transition: 'stroke-width .15s' }}
            onMouseMove={(e) => { setHover(i); tip.show(<><div className="t"><span className="ea-sw" style={{ background: d.c }} />{d.name}</div><div className="r"><span>Count</span><b>{fmt(d.v)}</b></div><div className="r"><span>Share</span><b>{((d.v / total) * 100).toFixed(1)}%</b></div></>, e.clientX, e.clientY); }}
            onMouseLeave={() => { setHover(null); tip.hide(); }} onClick={() => onPick?.(d.name)} />
        ); off += len; return el;
      })}
    </svg>
  );
}
function Legend({ items, onPick }: { items: DonutItem[]; onPick?: (name: string) => void }) {
  const total = items.reduce((a, b) => a + b.v, 0) || 1;
  return <div className="ea-legend">{items.map((d) => (
    <div className="ea-lg" key={d.name} onClick={() => onPick?.(d.name)}><span className="ea-sw" style={{ background: d.c }} /><span className="nm">{d.name}</span><span className="ct num">{fmt(d.v)}</span><span className="pc">{((d.v / total) * 100).toFixed(1)}%</span></div>
  ))}</div>;
}

/* ------------------------------------------------------------------ */
/* drawer                                                              */
/* ------------------------------------------------------------------ */
/** Compact bar chart — used for the per-error occurrence timeline in the drawer.
 *  Every bucket keeps its own x-axis label and carries its count above the bar; the
 *  track scrolls horizontally when a 30-day or 24-hour window won't fit the drawer. */
function MiniBars({ points, color, tip }: { points: { label: string; total: number }[]; color: string; tip: Tip }) {
  const max = Math.max(...points.map((p) => p.total), 1);
  return (
    <div className="ea-minibars-wrap">
      <div className="ea-minibars">
        {points.map((p, i) => (
          <div key={`${p.label}-${i}`} className="col"
            onMouseMove={(e) => tip.show(<><div className="t">{p.label}</div><div className="r"><span><span className="ea-sw" style={{ background: color }} />Occurrences</span><b>{fmt(p.total)}</b></div></>, e.clientX, e.clientY)}
            onMouseLeave={tip.hide}>
            {/* abbreviated so it fits the column; the tooltip carries the exact figure */}
            <span className="cnt" style={{ color: p.total > 0 ? color : 'transparent' }}>{fmtShort(p.total)}</span>
            <div className="bar">
              <i style={{ height: `${p.total > 0 ? Math.max((p.total / max) * 100, 3) : 0}%`, background: `linear-gradient(180deg, ${color}, ${color}99)` }} />
            </div>
            <span className="lab" title={p.label}>{p.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Occurrence timeline for a single error code, with its own horizontal date selection. */
function DrawerTimeline({ err, tip }: { err: CodeVM; tip: Tip }) {
  // Own window, independent of the page's date range; resets when a different code opens
  const [tlRange, setTlRange] = useState<TimeRange>('all');
  useEffect(() => { setTlRange('all'); }, [err.code]);

  const q = useQuery({
    queryKey: ['ea-code-timeline', err.code, tlRange],
    queryFn: () => errorLogsService.getDashboardData({ timeRange: tlRange, errorCode: err.code, includeArchive: false, page: 1, limit: 1 }),
    enabled: !!err.code,
    staleTime: 30_000,
  });
  const stats = q.data?.data;
  const points = stats?.timeline ?? [];
  const total = points.reduce((a, b) => a + b.total, 0);
  const peak = points.reduce((a, b) => Math.max(a, b.total), 0);
  const gran = stats?.timelineGranularity === 'minute' ? 'by interval' : stats?.timelineGranularity === 'hour' ? 'by hour' : 'by day';

  return (
    <div>
      <div className="ea-dsec">Occurrence timeline</div>

      {/* horizontal date selection — drives the graph below */}
      <div className="ea-seg ea-seg-full" role="group" aria-label="Timeline range">
        {TL_RANGES.map((t) => (
          <button key={t.value} className={tlRange === t.value ? 'on' : ''} onClick={() => setTlRange(t.value)}
            data-testid={`ErrorAnalytics.Drawer.Timeline.Range.${t.value}`}>{t.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '12px 0 8px' }}>
        <span style={{ fontSize: 11.5, color: 'var(--ea-ink-4)', fontWeight: 600 }}>
          {TL_RANGES.find((t) => t.value === tlRange)?.short} · {gran}
        </span>
        {peak > 0 && <span style={{ fontSize: 11.5, color: 'var(--ea-ink-4)', fontWeight: 600 }}>peak {fmt(peak)}</span>}
      </div>

      {q.isLoading ? (
        <div className="ea-minibars-empty">Loading…</div>
      ) : peak > 0 ? (
        <>
          <div style={{ fontSize: 20, fontWeight: 800, color: SEVC[err.sev], marginBottom: 8 }} className="num">{fmt(total)}<small style={{ fontSize: 11, fontWeight: 600, color: 'var(--ea-ink-4)', marginLeft: 6 }}>occurrences</small></div>
          <MiniBars points={points} color={SEVC[err.sev]} tip={tip} />
        </>
      ) : (
        <div className="ea-minibars-empty">No occurrences in the selected range.</div>
      )}
      <div className="ea-tline" style={{ marginTop: 16 }}>
        {([
          ['First seen', err.firstSeen ? new Date(err.firstSeen).toLocaleString() : '—', '#94A3B8'],
          ['Last seen', err.lastSeen ? new Date(err.lastSeen).toLocaleString() : err.last, SEVC[err.sev]],
        ] as [string, string, string][]).map((e, i) => (
          <div className="ev" key={i}><div className="d" style={{ background: e[2] }} /><div><div className="et">{e[0]}</div><div className="em">{e[1]}</div></div></div>
        ))}
      </div>
    </div>
  );
}

type DrawerTab = 'overview' | 'stack' | 'timeline' | 'related';
interface DrawerState { open: boolean; err: CodeVM; crumb: string[]; stack?: string; noFooter?: boolean; }
function DrawerPanel({ tab, err, stack, related, tip, onPick }: { tab: DrawerTab; err: CodeVM; stack?: string; related: CodeVM[]; tip: Tip; onPick: (c: CodeVM, label: string) => void }) {
  if (tab === 'overview') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div><div className="ea-dsec">Summary</div>
        <div className="ea-kv">
          <span className="k">Severity</span><span className="v">{err.sev}</span>
          <span className="k">Source</span><span className="v">{err.svc.join(', ') || '—'}</span>
          {err.isRecord ? (
            <>
              {/* the affected user for this specific event — name when resolvable, id always */}
              <span className="k">Affected user</span>
              <span className="v">{err.userId ? (err.userName || 'Unknown user') : 'Not attributed to a user'}</span>
              {err.endpoint && <><span className="k">Endpoint</span><span className="v mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{err.method ? `${err.method} ` : ''}{err.endpoint}</span></>}
              {err.env && <><span className="k">Environment</span><span className="v">{err.env}</span></>}
              {err.ip && err.ip !== '—' && <><span className="k">IP</span><span className="v mono">{err.ip}</span></>}
              <span className="k">Occurred at</span><span className="v">{err.lastSeen ? new Date(err.lastSeen).toLocaleString() : err.last}</span>
            </>
          ) : (
            <>
              <span className="k">Occurrences</span><span className="v num">{fmt(err.v)}</span>
              <span className="k">Affected users</span><span className="v num">{fmt(err.users)}</span>
              <span className="k">First seen</span><span className="v">{err.firstSeen ? new Date(err.firstSeen).toLocaleString() : '—'}</span>
              <span className="k">Last seen</span><span className="v">{err.lastSeen ? new Date(err.lastSeen).toLocaleString() : err.last}</span>
            </>
          )}
        </div></div>
      <div><div className="ea-dsec">Message</div><div style={{ fontSize: 12.5, color: 'var(--ea-ink-2)', lineHeight: 1.6 }}>{err.desc}</div></div>
      {err.params && Object.keys(err.params).length > 0 && (
        <div><div className="ea-dsec">Parameters</div>
          <div className="ea-params">
            {Object.entries(err.params).map(([k, v]) => (
              <Fragment key={k}>
                <span className="pk">{k}</span>
                <span className={`pv ${k === 'status_code' && Number(v) >= 400 ? 'code-403' : ''}`}>{typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}</span>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  if (tab === 'stack') return <div><div className="ea-dsec">Stack trace</div><pre className="ea-strace">{stack || 'No stack trace captured for this record.'}</pre></div>;
  if (tab === 'timeline') return <DrawerTimeline err={err} tip={tip} />;
  return <div><div className="ea-dsec">Related error codes</div><div className="ea-related">{related.filter((x) => x.code !== err.code).slice(0, 6).map((x) => <a key={x.code} onClick={() => onPick(x, x.code)}><span><SevChip s={x.sev} /> <span className="mono" style={{ marginLeft: 6 }}>{x.code}</span></span><b className="num">{fmt(x.v)}</b></a>)}</div></div>;
}

/* ================================================================== */
/* PAGE                                                                */
/* ================================================================== */
export default function ErrorAnalytics() {
  const [mainTab, setMainTab] = useState<'overview' | 'records' | 'params'>('overview');
  const [paramCode, setParamCode] = useState<string>('');
  const [paramKey, setParamKey] = useState<string>('');
  const [paramTopN, setParamTopN] = useState(10);
  // Custom window. Both null means all time, which is the default.
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  // '' = every project the caller may read, merged — what this dashboard has
  // always shown. Picking one narrows it; the server refuses to widen.
  const [appFilter, setAppFilter] = useState('');
  const [topN, setTopN] = useState(10);
  // What the Error Timeline breaks down by — one dimension at a time, never mixed
  const [tlMode, setTlMode] = useState<'severity' | 'source'>('source');
  const [tblSev, setTblSev] = useState<Sev | 'all'>('all');
  const [sort, setSort] = useState<{ col: string; dir: 1 | -1 }>({ col: 'eventDateTime', dir: -1 });
  const [page, setPage] = useState(0);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [srcType, setSrcType] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('');
  const [codeFilter, setCodeFilter] = useState<string>('');
  const [endpointFilter, setEndpointFilter] = useState<string>('');
  // Set when drilling in from Parameter Analysis — records carrying this parameter value
  const [paramFilter, setParamFilter] = useState<{ name: string; value: string } | null>(null);
  const [tip, setTip] = useState<{ show: boolean; x: number; y: number; content: TipContent }>({ show: false, x: 0, y: 0, content: null });
  const [listModal, setListModal] = useState<'codes' | 'users' | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [dTab, setDTab] = useState<DrawerTab>('overview');
  const [topUsers, setTopUsers] = useState<UserVM[]>(SAMPLE_USERS);
  const perPage = 10;
  const tipApi: Tip = useMemo(() => ({ show: (content, x, y) => setTip({ show: true, x, y, content }), hide: () => setTip((t) => ({ ...t, show: false })) }), []);

  /* The selected window, as the API wants it. Whole days: the start snaps to 00:00 and
     the end to 23:59:59 so a start == end selection covers that entire day. */
  const { fromDate, toDate, rangeLabel } = useMemo(() => {
    if (!startDate && !endDate) return { fromDate: undefined, toDate: undefined, rangeLabel: 'All time' };
    const start = startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : undefined;
    const end = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : undefined;
    const asLabel = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return {
      fromDate: start?.toISOString(),
      toDate: end?.toISOString(),
      // Only one end picked still filters — "from X onwards" / "up to Y"
      rangeLabel: start && end
        ? (start.toDateString() === end.toDateString() ? asLabel(start) : `${asLabel(start)} → ${asLabel(end)}`)
        : start ? `From ${asLabel(start)}` : `Up to ${asLabel(end!)}`,
    };
  }, [startDate, endDate]);
  // Every aggregation on this page is bounded by the same two things: the date
  // window and the project. They travel as one object so a query added later
  // cannot silently escape either.
  const baseParams = useMemo(
    () => ({ fromDate, toDate, ...(appFilter ? { appId: appFilter } : {}) }),
    [fromDate, toDate, appFilter],
  );

  /* Quick presets beside the two date fields. `days` counts back from today inclusive,
     so "Past 7 days" is today plus the six before it. null clears back to all time. */
  const applyPreset = (days: number | null) => {
    if (days === null) { setStartDate(null); setEndDate(null); }
    else {
      const end = new Date(); end.setHours(0, 0, 0, 0);
      const start = new Date(end); start.setDate(start.getDate() - (days - 1));
      setStartDate(start); setEndDate(end);
    }
    setPage(0);
  };
  const activePreset = useMemo(() => {
    if (!startDate && !endDate) return 'all';
    if (!startDate || !endDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (endDate.toDateString() !== today.toDateString()) return null;
    const spanDays = Math.round((today.getTime() - new Date(startDate).setHours(0, 0, 0, 0)) / 86400000) + 1;
    return spanDays === 7 ? '7d' : spanDays === 30 ? '30d' : null;
  }, [startDate, endDate]);

  // Expanded inline, so it stays open while the page is used; Escape collapses it.
  useEffect(() => {
    if (!dateOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDateOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dateOpen]);

  /* ---- live queries ---- */
  const dashQ = useQuery({
    queryKey: ['ea-dashboard', fromDate, toDate, topN, appFilter],
    queryFn: () => errorLogsService.getDashboardData({ ...baseParams, includeArchive: false, page: 1, limit: topN }),
    staleTime: 30_000,
  });
  // Records — server-side paginated + range-filtered via POST /search (the reliable path the monitor uses;
  // GET /list was throwing ERR.CORE.ERROR_LOG.SEARCH.FAILED).
  const searchQ = useQuery({
    queryKey: ['ea-records', fromDate, toDate, tblSev, srcType, userFilter, codeFilter, endpointFilter, paramFilter, q, page, sort, appFilter],
    queryFn: () =>
      errorLogsService.searchRecords({
        page: page + 1, pageSize: perPage,
        q: q || undefined,
        errorSeverity: tblSev === 'all' ? undefined : tblSev.toUpperCase(),
        sourceType: srcType === 'all' ? undefined : srcType,
        actorUserId: userFilter || undefined,
        errorCode: codeFilter || undefined,
        endpoint: endpointFilter || undefined,
        sort_by: sort.col, sort_order: sort.dir > 0 ? 'asc' : 'desc',
        includeArchive: false,
        // Server converts these to IST; both absent means no time filter.
        ...baseParams,
        paramName: paramFilter?.name,
        paramValue: paramFilter?.value,
      }),
    staleTime: 15_000,
  });
  // The timeline and the severity split both come from the dashboard aggregation
  // (`timeline` / `bySeverity`), which counts every matching document server-side for
  // the selected range. They used to be computed from a page of raw records, which the
  // API caps — so both charts silently under-reported once a range held more errors
  // than a single page.
  useEffect(() => { const id = setTimeout(() => { setQ(qInput); setPage(0); }, 350); return () => clearTimeout(id); }, [qInput]);
  useEffect(() => { setListSearch(''); }, [listModal]);

  // Full error-code list for the "Unique Errors" drill-in (the overview cards only
  // hold the current Top-N page, which is not the whole set).
  const allCodesQ = useQuery({
    queryKey: ['ea-all-codes', fromDate, toDate, appFilter],
    queryFn: () => errorLogsService.getDashboardData({ ...baseParams, includeArchive: false, page: 1, limit: 200 }),
    enabled: listModal === 'codes' || mainTab === 'params',
    staleTime: 30_000,
  });

  // A record drawer shows aggregates for its error code (a single event has none of its
  // own), so fetch that code's totals for the active range.
  const drawerCodeQ = useQuery({
    queryKey: ['ea-code-summary', drawer?.err.code, fromDate, toDate, appFilter],
    queryFn: () => errorLogsService.getDashboardData({ ...baseParams, errorCode: drawer!.err.code, includeArchive: false, page: 1, limit: 1 }),
    enabled: !!drawer?.open && !!drawer?.err.isRecord && !!drawer?.err.code,
    staleTime: 30_000,
  });
  const drawerCodeSummary = drawerCodeQ.data?.data?.summary;

  /* ---- parameter analysis ---- */
  // Available parameter names on the chosen error code
  const paramKeysQ = useQuery({
    queryKey: ['ea-param-keys', paramCode, fromDate, toDate, appFilter],
    queryFn: () => errorLogsService.getParameterAnalysis({ errorCode: paramCode, ...baseParams }),
    enabled: mainTab === 'params' && !!paramCode,
    staleTime: 30_000,
  });
  const paramKeys = useMemo(
    () => (paramKeysQ.data?.data?.parameters ?? []).filter((p) => !USER_ID_KEY.test(p.parameter)),
    [paramKeysQ.data],
  );
  // Top values of the chosen parameter
  const paramValuesQ = useQuery({
    queryKey: ['ea-param-values', paramCode, paramKey, fromDate, toDate, paramTopN, appFilter],
    queryFn: () => errorLogsService.getParameterAnalysis({ errorCode: paramCode, parameter: paramKey, ...baseParams, limit: paramTopN }),
    enabled: mainTab === 'params' && !!paramCode && !!paramKey,
    staleTime: 30_000,
  });
  const paramResult = paramValuesQ.data?.data;
  // Selecting a different code invalidates the parameter choice
  useEffect(() => { setParamKey(''); }, [paramCode]);
  // Default to the first parameter once the list loads
  useEffect(() => { if (!paramKey && paramKeys.length) setParamKey(paramKeys[0].parameter); }, [paramKeys, paramKey]);
  useEffect(() => { const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer((d) => (d ? { ...d, open: false } : d)); }; window.addEventListener('keydown', onEsc); return () => window.removeEventListener('keydown', onEsc); }, []);

  const stats: ErrorLogDashboardStats | undefined = dashQ.data?.data;
  const hasStats = !!stats && (stats.summary?.totalErrors ?? 0) > 0;

  // Top Users — server-side aggregation (stats.topUsers) with resolved names,
  // exactly like the Error-Logs Monitor page.
  useEffect(() => {
    const raw: { userId: string; count: number }[] = (stats as unknown as { topUsers?: { userId: string; count: number }[] })?.topUsers ?? [];
    if (!raw.length) { setTopUsers(SAMPLE_USERS); return; }
    let cancelled = false;
    (async () => {
      try {
        const nameMap = await personnelLookupService.getNamesByIds(raw.map((u) => u.userId));
        if (!cancelled) setTopUsers(raw.map((u) => ({ userId: u.userId, user: nameMap.get(u.userId) || 'Unknown user', v: u.count })));
      } catch {
        if (!cancelled) setTopUsers(raw.map((u) => ({ userId: u.userId, user: 'Unknown user', v: u.count })));
      }
    })();
    return () => { cancelled = true; };
  }, [stats]);

  /* ---- view models ---- */
  // Timeline points come straight from the server buckets for the selected range —
  // every bucket is a real timestamp, so 7d and 30d no longer render the same shape.
  const trendPoints: TLPoint[] = useMemo(() => {
    const buckets = stats?.timeline ?? [];
    if (!buckets.length) return hasStats ? [] : sampleTrend();
    return buckets.map((b) => ({
      label: b.label,
      total: b.total,
      parts: Object.entries(b.bySourceType ?? {})
        .map(([name, v]) => ({ name, v, c: srcColor(name) }))
        .filter((p) => p.v > 0)
        .sort((a, b2) => b2.v - a.v),
    }));
  }, [stats, hasStats]);

  /* Lines drawn over the timeline — only the selected dimension, one line per level or
     per source type. The two dimensions are never plotted together. */
  const trendSeries: TLSeries[] = useMemo(() => {
    const buckets = stats?.timeline ?? [];
    if (!buckets.length) return [];
    const out: TLSeries[] = [];

    if (tlMode === 'severity') {
      SEV_ORDER.forEach((s) => {
        const values = buckets.map((b) => b.bySeverity?.[s.toUpperCase()] ?? 0);
        if (values.some((v) => v > 0)) out.push({ name: s, color: SEVC[s], values, group: 'severity' });
      });
    } else {
      const names = [...new Set(buckets.flatMap((b) => Object.keys(b.bySourceType ?? {})))];
      names.forEach((n) => {
        const values = buckets.map((b) => b.bySourceType?.[n] ?? 0);
        if (values.some((v) => v > 0)) out.push({ name: n, color: srcColor(n), values, group: 'source' });
      });
    }
    return out;
  }, [stats, tlMode]);

  const distItems: DonutItem[] = useMemo(() => {
    if (hasStats) {
      const items = SEV_ORDER
        .map((s) => ({ name: s, v: stats!.bySeverity?.[s.toUpperCase()]?.count ?? 0, c: SEVC[s] }))
        .filter((d) => d.v > 0);
      if (items.length) return items;
    }
    return SAMPLE_DIST;
  }, [hasStats, stats]);
  const srcItems: DonutItem[] = useMemo(() => (hasStats && stats!.byType) ? Object.entries(stats!.byType).map(([name, s]) => ({ name, v: s.count, c: srcColor(name) })).filter((d) => d.v > 0).sort((a, b) => b.v - a.v) : SAMPLE_SRC, [hasStats, stats]);

  const codeItems: CodeVM[] = useMemo(() => {
    if (!hasStats || !stats!.errorGroups?.length) return SAMPLE_CODES;
    return stats!.errorGroups.map((g) => ({ code: g.errorCode, desc: g.resolvedMessage || g.message || '—', v: g.occurrences, sev: toSev(g.severity), svc: [g.sourceName].filter(Boolean), last: timeAgo(g.lastSeen), extra: `${fmt(g.affectedUsers)} users`, users: g.affectedUsers, type: g.sourceType, endpoint: (g as unknown as { endpoint?: string }).endpoint, firstSeen: g.firstSeen, lastSeen: g.lastSeen, raw: g }));
  }, [hasStats, stats]);

  const endpointItems: EndpointVM[] = useMemo(() => {
    if (!hasStats || !stats!.errorGroups?.length) return SAMPLE_ENDPOINTS;
    const agg = new Map<string, { v: number; codes: Set<string>; sevRank: number; sev: Sev; last: string }>();
    stats!.errorGroups.forEach((g) => {
      const key = (g as unknown as { endpoint?: string }).endpoint || g.sourceName || g.sourceType || '—';
      const cur = agg.get(key) ?? { v: 0, codes: new Set<string>(), sevRank: 99, sev: 'Low' as Sev, last: '' };
      cur.v += g.occurrences; cur.codes.add(g.errorCode);
      const rank = SEV_ORDER.indexOf(toSev(g.severity)); if (rank < cur.sevRank) { cur.sevRank = rank; cur.sev = toSev(g.severity); }
      if (!cur.last || new Date(g.lastSeen).getTime() > new Date(cur.last).getTime()) cur.last = g.lastSeen;
      agg.set(key, cur);
    });
    return [...agg.entries()].map(([name, d]) => ({ name, v: d.v, sev: d.sev, codes: d.codes.size, last: timeAgo(d.last) })).sort((a, b) => b.v - a.v);
  }, [hasStats, stats]);

  const kpis: KpiVM[] = useMemo(() => {
    if (!hasStats) return [
      { key: 'total', label: 'Total Errors', val: 48219, color: C.indigo, tip: 'All error events in the selected window' },
      { key: 'critical', label: 'Critical Errors', val: 1284, color: C.red, tip: 'Severity = Critical' },
      { key: 'high', label: 'High Errors', val: 6820, color: C.amber, tip: 'Severity = High' },
      { key: 'unique', label: 'Unique Errors', val: 312, color: C.violet, tip: 'Distinct error codes' },
      { key: 'users', label: 'Affected Users', val: 4120, color: C.cyan, tip: 'Distinct impacted users' },
    ];
    const sum = stats!.summary, bySev = stats!.bySeverity ?? {}, bySevType = stats!.bySeverityType ?? {};
    const toSources = (m: Record<string, number> | undefined) =>
      Object.entries(m ?? {}).map(([name, v]) => ({ name, v })).filter((s) => s.v > 0).sort((a, b) => b.v - a.v);
    // Total Errors spans every severity, so its split is byType
    const totalSources = toSources(Object.fromEntries(Object.entries(stats!.byType ?? {}).map(([k, v]) => [k, v.count])));
    return [
      { key: 'total', label: 'Total Errors', val: sum.totalErrors, color: C.indigo, tip: 'All error events in the selected window', sources: totalSources },
      { key: 'critical', label: 'Critical Errors', val: bySev.CRITICAL?.count ?? 0, color: SEVC.Critical, tip: 'Severity = Critical', sources: toSources(bySevType.CRITICAL) },
      { key: 'high', label: 'High Errors', val: bySev.HIGH?.count ?? 0, color: SEVC.High, tip: 'Severity = High', sources: toSources(bySevType.HIGH) },
      { key: 'unique', label: 'Unique Errors', val: sum.totalUniqueErrors, color: C.violet, tip: 'Distinct error codes', drill: { text: 'View error codes', open: () => setListModal('codes') } },
      { key: 'users', label: 'Affected Users', val: sum.totalAffectedUsers, color: C.cyan, tip: 'Distinct impacted users', drill: { text: 'View affected users', open: () => setListModal('users') } },
    ];
  }, [hasStats, stats]);

  /* table rows */
  const searchRes = searchQ.data as { data?: ErrorLog[]; pagination?: { totalItems?: number } } | ErrorLog[] | undefined;
  const liveRows: ErrorLog[] | undefined = Array.isArray(searchRes) ? searchRes : searchRes?.data;
  const serverTotal: number | undefined = Array.isArray(searchRes) ? searchRes.length : searchRes?.pagination?.totalItems;
  const { pageRows, totalRows } = useMemo(() => {
    const mapRow = (r: ErrorLog): TableVM => ({ ts: new Date(r.eventDateTime), source: r.sourceName, type: r.sourceType, sev: toSev(r.errorSeverity), code: r.errorCode, env: r.environment, user: r.actorUserId || '—', ip: r.ip || '—', msg: r.resolvedMessage || r.message || (r as unknown as { parameters?: { detail?: string } }).parameters?.detail || '—', raw: r });
    if (liveRows && liveRows.length) return { pageRows: liveRows.map(mapRow), totalRows: serverTotal ?? liveRows.length };
    const ql = q.trim().toLowerCase();
    const filtered = sampleRows(20260731).filter((r) => (tblSev === 'all' || r.sev === tblSev) && (srcType === 'all' || r.type === srcType)
      && (!codeFilter || r.code === codeFilter) && (!ql || r.code.toLowerCase().includes(ql) || r.msg.toLowerCase().includes(ql)))
      .sort((x, y) => { let a: number | string, b: number | string; if (sort.col === 'errorSeverity') { a = SEV_ORDER.indexOf(x.sev); b = SEV_ORDER.indexOf(y.sev); } else if (sort.col === 'errorCode') { a = x.code; b = y.code; } else if (sort.col === 'sourceName') { a = x.source; b = y.source; } else { a = x.ts.getTime(); b = y.ts.getTime(); } return typeof a === 'string' ? a.localeCompare(b as string) * sort.dir : ((a as number) - (b as number)) * sort.dir; });
    return { pageRows: filtered.slice(page * perPage, (page + 1) * perPage), totalRows: filtered.length };
  }, [liveRows, serverTotal, tblSev, srcType, codeFilter, q, sort, page]);

  // Resolve the actorUserIds on the visible page so records show a person, not a raw id
  const [recNames, setRecNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const ids = [...new Set(pageRows.map((r) => r.user).filter((u) => u && u !== '—'))];
    if (!ids.length) return;
    let cancelled = false;
    personnelLookupService.getNamesByIds(ids)
      .then((m) => { if (!cancelled) setRecNames((prev) => new Map([...prev, ...m])); })
      .catch(() => { /* fall back to showing the raw id */ });
    return () => { cancelled = true; };
  }, [pageRows]);

  /* ---- interactions ---- */
  const openCode = (err: CodeVM, crumb: string[]) => { setDrawer({ open: true, err, crumb }); setDTab('overview'); };
  const openLog = (r: TableVM) => {
    const raw = r.raw as unknown as { parameters?: unknown; endpoint?: string; method?: string } | undefined;
    const rawParams = r.raw ? (raw?.parameters ?? r.raw.parametersJson) : undefined;
    // `parameters` is stored either as an object or as a list of {name, value} pairs
    const params = normaliseParams(rawParams);
    const userId = r.user && r.user !== '—' ? r.user : undefined;
    const err: CodeVM = {
      code: r.code, desc: r.msg, v: 1, sev: r.sev, svc: [r.source], last: fmtTime(r.ts),
      extra: r.user, users: 0, type: r.type, endpoint: raw?.endpoint, params,
      firstSeen: r.raw?.eventDateTime, lastSeen: r.raw?.eventDateTime,
      isRecord: true, userId, userName: userId ? recNames.get(userId) : undefined,
      env: r.env, method: raw?.method, ip: r.ip,
    };
    setDrawer({ open: true, err, crumb: ['Records', r.code], stack: r.raw?.stack || r.raw?.stackTrace, noFooter: true }); setDTab('overview');
  };
  const closeDrawer = () => setDrawer((d) => (d ? { ...d, open: false } : d));
  const setSortCol = (col: string) => setSort((s) => (s.col === col ? { col, dir: (s.dir * -1) as 1 | -1 } : { col, dir: 1 }));

  /* Overview → Records drill-through: every breakdown jumps to the record stream
     with just that dimension applied, replacing whatever was filtered before. */
  const drillTo = (patch: Partial<{ sev: Sev | 'all'; src: string; user: string; code: string; endpoint: string; param: { name: string; value: string } }>) => {
    setTblSev(patch.sev ?? 'all');
    setSrcType(patch.src ?? 'all');
    setUserFilter(patch.user ?? '');
    setCodeFilter(patch.code ?? '');
    setEndpointFilter(patch.endpoint ?? '');
    setParamFilter(patch.param ?? null);
    setQInput(''); setQ('');
    setPage(0);
    setMainTab('records');
  };
  const filterBySeverity = (name: string) => { if (SEV_ORDER.includes(name as Sev)) drillTo({ sev: name as Sev }); };
  const filterBySource = (name: string) => drillTo({ src: name });
  const filterByCode = (code: string) => drillTo({ code });
  const filterByEndpoint = (endpoint: string) => drillTo({ endpoint });
  const filterByUser = (userId: string) => drillTo({ user: userId });

  const activeFilters: { key: string; label: string; clear: () => void }[] = [
    ...(tblSev !== 'all' ? [{ key: 'sev', label: `Severity: ${tblSev}`, clear: () => setTblSev('all') }] : []),
    ...(srcType !== 'all' ? [{ key: 'src', label: `Source: ${srcType}`, clear: () => setSrcType('all') }] : []),
    ...(codeFilter ? [{ key: 'code', label: `Code: ${codeFilter}`, clear: () => setCodeFilter('') }] : []),
    ...(endpointFilter ? [{ key: 'ep', label: `Endpoint: ${endpointFilter}`, clear: () => setEndpointFilter('') }] : []),
    ...(paramFilter ? [{ key: 'param', label: `${paramFilter.name}: ${paramFilter.value}`, clear: () => setParamFilter(null) }] : []),
    ...(userFilter ? [{ key: 'user', label: `User: ${topUsers.find((u) => u.userId === userFilter)?.user || 'Unknown user'}`, clear: () => setUserFilter('') }] : []),
    ...(q ? [{ key: 'q', label: `Search: ${q}`, clear: () => { setQInput(''); setQ(''); } }] : []),
  ].map((f) => ({ ...f, clear: () => { f.clear(); setPage(0); } }));
  const clearAllFilters = () => { setTblSev('all'); setSrcType('all'); setUserFilter(''); setCodeFilter(''); setEndpointFilter(''); setParamFilter(null); setQInput(''); setQ(''); setPage(0); };

  /* rows behind the Unique Errors / Affected Users drill-ins, filtered by the modal search */
  const listRows = useMemo(() => {
    const needle = listSearch.trim().toLowerCase();
    if (listModal === 'codes') {
      const groups = allCodesQ.data?.data?.errorGroups ?? stats?.errorGroups ?? [];
      return groups
        .map((g) => ({ key: g.errorCode, primary: g.errorCode, secondary: g.resolvedMessage || g.message || '—', v: g.occurrences, sev: toSev(g.severity), pick: () => filterByCode(g.errorCode) }))
        .filter((r) => !needle || r.primary.toLowerCase().includes(needle) || r.secondary.toLowerCase().includes(needle));
    }
    if (listModal === 'users') {
      return topUsers
        .map((u) => ({ key: u.userId, primary: u.user, secondary: `${u.v.toLocaleString()} errors`, v: u.v, sev: undefined as Sev | undefined, pick: () => filterByUser(u.userId) }))
        .filter((r) => !needle || r.primary.toLowerCase().includes(needle) || r.secondary.toLowerCase().includes(needle));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listModal, listSearch, allCodesQ.data, stats, topUsers]);

  // Error codes offered in the Parameter Analysis dropdown — the full list, not just Top-N
  const paramCodeOptions = useMemo(() => {
    const groups = allCodesQ.data?.data?.errorGroups ?? stats?.errorGroups ?? [];
    return groups.map((g) => ({ label: `${g.errorCode}  (${fmt(g.occurrences)})`, value: g.errorCode }));
  }, [allCodesQ.data, stats]);

  const distTotal = distItems.reduce((a, b) => a + b.v, 0);
  const rangeTotal = hasStats ? (stats!.summary?.totalErrors ?? 0) : trendPoints.reduce((a, b) => a + b.total, 0);
  const codeMax = codeItems[0]?.v || 1;
  const endpointMax = endpointItems[0]?.v || 1;
  /* Grid rows need a stable key; TableVM carries none of its own. Columns are keyed
     by the API field names so the sort state maps straight onto the search call.
     Per-column filters are off — the records are filtered from the toolbar above. */
  const gridRows = pageRows.map((r, i) => ({ ...r, _key: `${r.code}-${r.ts.getTime()}-${i}` }));
  type GridRow = (typeof gridRows)[number];
  const recordColumns: SaasColumn<GridRow>[] = [
    { field: 'eventDateTime', header: 'Timestamp', sortable: true, hideable: false, style: { whiteSpace: 'nowrap', width: 170 }, body: (r) => <span className="num" style={{ color: '#64748B' }}>{fmtTime(r.ts)}</span> },
    { field: 'sourceName', header: 'Source', sortable: true, body: (r) => <b style={{ color: '#0B1220' }}>{r.source}</b> },
    { field: 'sourceType', header: 'Type', body: (r) => <span className="host">{r.type}</span> },
    { field: 'errorSeverity', header: 'Severity', sortable: true, align: 'center', body: (r) => <SevChip s={r.sev} /> },
    { field: 'errorCode', header: 'Error Code', sortable: true, style: { minWidth: 180 }, body: (r) => <span className="code">{r.code}</span> },
    { field: 'environment', header: 'Environment', body: (r) => <span className="host">{r.env}</span> },
    { field: 'actorUserId', header: 'User', body: (r) => <span className="host">{r.user !== '—' ? (recNames.get(r.user) || 'Unknown user') : '—'}</span> },
    { field: 'ip', header: 'IP', body: (r) => <span className="host">{r.ip}</span> },
    { field: 'message', header: 'Message', style: { minWidth: 260, maxWidth: 380 }, body: (r) => <span className="msg" title={r.msg}>{r.msg}</span> },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 80 },
      body: (r) => <button className="ea-iconbtn" style={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); openLog(r); }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15"><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></svg></button>,
    },
  ];

  return (
    <div className="ea-root">
      <div className="ea-stack">
        {/* head */}
        <div className="ea-head">
          <div>
            <div className="ea-crumb"><b>Error Log Dashboard</b><span className="sep">/</span>{rangeLabel}{!hasStats && <><span className="sep">·</span><span style={{ color: C.amber }}>sample data</span></>}</div>
            <h1>Error Log Dashboard</h1>
          </div>
          {/* Project and date range are one control group, pinned together at the
              right. `.ea-head` is space-between, so leaving them as separate
              children of it pushed the project picker into the middle of the
              header, adrift from the range it belongs beside. */}
          <div className="ea-headctl">
          <ProjectFilter value={appFilter} onChange={setAppFilter} testId="ErrorAnalytics.ProjectFilter" />
          {/* Collapsed to the active window; expands to the start/end fields and presets.
              Everything between the two dates is included; both empty means all time. */}
          <div className="ea-daterange">
            {/* Collapses to a bare arrow once open, so the fields fit on the same line */}
            <button className={`ea-dr-trigger ${dateOpen ? 'open' : ''}`} onClick={() => setDateOpen((o) => !o)}
              aria-expanded={dateOpen} aria-label={dateOpen ? 'Hide date range' : 'Change date range'}
              title={dateOpen ? 'Hide date range' : 'Change date range'} data-testid="ErrorAnalytics.DateRange">
              {!dateOpen && (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" width="15"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /></svg>
                  <span className="l">Date range</span>
                  <b>{rangeLabel}</b>
                </>
              )}
              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13"><path d="m9 6 6 6-6 6" /></svg>
            </button>

            {dateOpen && (
              <div className="ea-dr-pop">
                <div className="fields">
                  <div className="fld">
                    <span className="lbl">Start date</span>
                    <Calendar
                      value={startDate}
                      onChange={(e: { value: Date | null }) => { setStartDate(e.value ?? null); setPage(0); }}
                      showIcon showButtonBar dateFormat="dd M yy" placeholder="Any"
                      maxDate={endDate ?? new Date()} appendTo={typeof document !== 'undefined' ? document.body : undefined}
                      testId="ErrorAnalytics.StartDate" style={{ width: 150 }}
                    />
                  </div>
                  <div className="fld">
                    <span className="lbl">End date</span>
                    <Calendar
                      value={endDate}
                      onChange={(e: { value: Date | null }) => { setEndDate(e.value ?? null); setPage(0); }}
                      showIcon showButtonBar dateFormat="dd M yy" placeholder="Any"
                      minDate={startDate ?? undefined} maxDate={new Date()}
                      appendTo={typeof document !== 'undefined' ? document.body : undefined}
                      testId="ErrorAnalytics.EndDate" style={{ width: 150 }}
                    />
                  </div>
                </div>
                <div className="presets">
                  <span className="lbl">Quick select</span>
                  <div className="ea-seg">
                    {([['7d', 'Past 7 days', 7], ['30d', 'Past 30 days', 30], ['all', 'All time', null]] as const).map(([key, label, days]) => (
                      <button key={key} className={activePreset === key ? 'on' : ''} onClick={() => applyPreset(days)}
                        data-testid={`ErrorAnalytics.DateRange.${key}`}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* KPIs — count only, one line */}
        <div className="ea-kpis">
          {kpis.map((k, i) => (
            <div key={k.key} className="ea-card ea-kpi ea-reveal" style={{ animationDelay: `${i * 40}ms`, ['--kc' as string]: k.color, ['--kglow' as string]: `${k.color}99` } as CSSProperties}
              onMouseMove={(e) => tipApi.show(<><div className="t">{k.label}</div><div className="r">{k.tip}</div></>, e.clientX, e.clientY)} onMouseLeave={tipApi.hide}>
              {/* icon on the left, heading + count stacked beside it */}
              <div className="top">
                <div className="ic" style={{ background: `linear-gradient(135deg, ${k.color}, ${k.color}bb)` }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="17"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg></div>
                <div className="ea-kpi-meta">
                  <div className="lab">{k.label}</div>
                  <div className="val num"><CountUp value={k.val} unit={k.unit} /></div>
                </div>
              </div>
              {/* source split below, aligned to the right */}
              {k.sources && k.sources.length > 0 && (
                <div className="ea-kpi-src">
                  {k.sources.slice(0, 3).map((s) => (
                    <button key={s.name} className="row" title={`${fmt(s.v)} ${s.name} errors — show records`}
                      onClick={(e) => { e.stopPropagation(); drillTo({ src: s.name, sev: k.key === 'critical' ? 'Critical' : k.key === 'high' ? 'High' : 'all' }); }}>
                      <span className="dot" style={{ background: srcColor(s.name) }} />
                      <span className="nm">{s.name}</span>
                      <span className="ct num">{fmt(s.v)}</span>
                    </button>
                  ))}
                  {k.sources.length > 3 && <span className="more">+{k.sources.length - 3} more</span>}
                </div>
              )}
              {/* drill-in affordance for the list-backed KPIs */}
              {k.drill && (
                <button className="ea-kpi-drill" onClick={(e) => { e.stopPropagation(); k.drill!.open(); }}>
                  {k.drill.text}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* main tabs */}
        <div className="ea-maintabs">
          <button className={mainTab === 'overview' ? 'on' : ''} onClick={() => setMainTab('overview')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>Overview
          </button>
          <button className={mainTab === 'records' ? 'on' : ''} onClick={() => setMainTab('records')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></svg>Records<span className="cnt num">{fmt(totalRows)}</span>
          </button>
          <button className={mainTab === 'params' ? 'on' : ''} onClick={() => setMainTab('params')} data-testid="ErrorAnalytics.Tab.Params">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M4 6h16M4 12h10M4 18h7" /><circle cx="18" cy="12" r="2.2" /><circle cx="14" cy="18" r="2.2" /></svg>Parameter Analysis
          </button>
        </div>

        {mainTab === 'overview' ? (
          <>

            {/* timeline (full width) */}
            <section className="ea-card pad ea-reveal">
              <div className="ea-card-head">
                <div><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M3 12h4l2 6 4-14 2 8h6" /></svg></span>Error Timeline</div><div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>{rangeLabel === 'All time' ? 'Errors over all time' : `Errors from ${rangeLabel}`} — {stats?.timelineGranularity === 'minute' ? 'by interval' : stats?.timelineGranularity === 'day' ? 'by day' : 'by hour'}</div></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div className="ea-seg" role="group" aria-label="Timeline breakdown">
                    {([['severity', 'Error Severity'], ['source', 'Source']] as const).map(([v, l]) => (
                      <button key={v} className={tlMode === v ? 'on' : ''} onClick={() => setTlMode(v)} data-testid={`ErrorAnalytics.Timeline.Mode.${v}`}>{l}</button>
                    ))}
                  </div>
                  <div className="ea-tl-stat"><div className="n num">{fmt(rangeTotal)}</div><div className="l">errors · {rangeLabel.toLowerCase()}</div></div>
                </div>
              </div>
              <Timeline points={trendPoints} series={trendSeries} tip={tipApi} />
              {trendSeries.length > 0 && (
                <div className="ea-tl-legend">
                  {trendSeries.map((s) => (
                    <span className="lg" key={`${s.group}-${s.name}`}>
                      <svg width="18" height="8" aria-hidden><line x1="0" y1="4" x2="18" y2="4" stroke={s.color} strokeWidth="2.6" strokeLinecap="round" /></svg>
                      {s.name}
                    </span>
                  ))}
                  <span className="hint">{tlMode === 'severity' ? 'one line per severity level' : 'one line per source type'}</span>
                </div>
              )}
            </section>

            {/* severity + sources side by side */}
            <div className="ea-grid">
              <section className="ea-card pad ea-c6 ea-reveal">
                <div className="ea-card-head"><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#06B6D4,#3B82F6)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><circle cx="12" cy="12" r="9" /></svg></span>Severity Distribution</div></div>
                <div className="ea-donut-wrap">
                  <div style={{ position: 'relative', flex: 'none' }}><Donut items={distItems} tip={tipApi} onPick={filterBySeverity} /><div className="ea-donut-c"><div><div className="big num">{fmt(distTotal)}</div><div className="cap">Total</div></div></div></div>
                  <Legend items={distItems} onPick={filterBySeverity} />
                </div>
              </section>
              <section className="ea-card pad ea-c6 ea-reveal">
                <div className="ea-card-head"><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#14B8A6,#0EA5E9)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><path d="M12 2a10 10 0 1 0 10 10H12Z" /></svg></span>Error Sources</div></div>
                <div className="ea-donut-wrap">
                  <div style={{ position: 'relative', flex: 'none' }}><Donut items={srcItems} tip={tipApi} onPick={filterBySource} /><div className="ea-donut-c"><div><div className="big num">{srcItems.length}</div><div className="cap">Sources</div></div></div></div>
                  <Legend items={srcItems} onPick={filterBySource} />
                </div>
              </section>
            </div>

            {/* top error codes + top endpoints side by side */}
            <div className="ea-grid">
              <section className="ea-card pad ea-c6 ea-reveal">
                <div className="ea-card-head"><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#EF4444,#F59E0B)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M4 4h16M4 4v16M8 8h9M8 12h6M8 16h9" /></svg></span>Top Error Codes</div>
                  <div className="ea-seg">{[10, 25, 50].map((n) => <button key={n} className={topN === n ? 'on' : ''} onClick={() => setTopN(n)}>Top {n}</button>)}</div></div>
                <div className="ea-scrolllist">
                {codeItems.slice(0, topN).map((c, i) => (
                  <div className="ea-rank" key={c.code + i} onClick={() => filterByCode(c.code)} title={`Show records for ${c.code}`}>
                    <div className="rk">{String(i + 1).padStart(2, '0')}</div>
                    <div className="body">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}><span className="code">{c.code}</span><SevChip s={c.sev} />{c.endpoint && <span className="host" title={c.endpoint}>{c.endpoint}</span>}</div>
                      <div className="desc">{c.desc}</div>
                      <div className="prog"><i style={{ width: `${(c.v / codeMax) * 100}%`, background: SEVC[c.sev], animationDelay: `${i * 40}ms` }} /></div>
                    </div>
                    <div className="rt">
                      <div className="oc num">{fmt(c.v)}<small>{fmt(c.users)} users · {c.last}</small></div>
                      <button aria-label="Open details" className="ea-iconbtn" style={{ width: 26, height: 26 }} title="Open details"
                        onClick={(e) => { e.stopPropagation(); openCode(c, ['Overview', 'Top Codes', c.code]); }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" strokeLinecap="round" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
                </div>
              </section>
              <section className="ea-card pad ea-c6 ea-reveal">
                <div className="ea-card-head"><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#6366F1,#A855F7)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg></span>Top Endpoints</div><span className="ea-sub">by error volume</span></div>
                <div className="ea-scrolllist">
                {endpointItems.slice(0, topN).map((e, i) => (
                  <div className="ea-rank" key={e.name + i} onClick={() => filterByEndpoint(e.name)} title={`Show records for ${e.name}`}>
                    <div className="rk">{String(i + 1).padStart(2, '0')}</div>
                    <div className="body">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}><span className="code">{e.name}</span><SevChip s={e.sev} /></div>
                      <div className="desc">{e.codes} distinct error {e.codes === 1 ? 'code' : 'codes'} · {e.last}</div>
                      <div className="prog"><i style={{ width: `${(e.v / endpointMax) * 100}%`, background: SEVC[e.sev], animationDelay: `${i * 40}ms` }} /></div>
                    </div>
                    <div className="rt"><div className="oc num">{fmt(e.v)}<small>errors</small></div></div>
                  </div>
                ))}
                </div>
              </section>
            </div>

            {/* top users — full page width, compact grid */}
            <section className="ea-card pad ea-reveal">
              <div className="ea-card-head"><div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#14B8A6,#6366F1)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></svg></span>Top Users</div><span className="ea-sub">by error count · {topUsers.length} {topUsers.length === 1 ? 'user' : 'users'}</span></div>
              <div className="ea-users">
                {topUsers.slice(0, topN).map((u, i) => (
                  <div className="ea-user" key={u.userId + i} onClick={() => filterByUser(u.userId)} title={`Show records for ${u.user}`}>
                    <span className="rk">#{i + 1}</span>
                    <span className="ea-avatar" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>{initials(u.user)}</span>
                    <span className="nm">{u.user}</span>
                    <span className="cnt num">{fmt(u.v)}</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : mainTab === 'params' ? (
          /* ---------------- PARAMETER ANALYSIS TAB ---------------- */
          <section className="ea-card pad ea-reveal">
            <div className="ea-card-head">
              <div>
                <div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#0EA5E9,#8B5CF6)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M4 6h16M4 12h10M4 18h7" /><circle cx="18" cy="12" r="2.2" /><circle cx="14" cy="18" r="2.2" /></svg></span>Parameter Analysis</div>
                <div className="ea-sub" style={{ marginTop: 4, marginLeft: 36 }}>Pick an error code, then a parameter from its payload — the most frequent values are ranked by occurrence.</div>
              </div>
              {paramResult?.values?.length ? <div className="ea-seg">{[10, 25, 50].map((n) => <button key={n} className={paramTopN === n ? 'on' : ''} onClick={() => setParamTopN(n)}>Top {n}</button>)}</div> : null}
            </div>

            <div className="ea-paramsel">
              <label>
                <span className="l">Error code</span>
                <Dropdown value={paramCode}
                  options={[{ label: 'Select an error code…', value: '' }, ...paramCodeOptions]}
                  optionLabel="label" optionValue="value" filter filterPlaceholder="Search error code…" resetFilterOnHide
                  placeholder="Select an error code…" onChange={(e: { value: string }) => setParamCode(e.value || '')}
                  style={{ width: '100%' }} />
              </label>
              <label>
                <span className="l">Parameter</span>
                <Dropdown value={paramKey}
                  options={paramKeys.map((p) => ({ label: `${p.parameter}  (${fmt(p.count)})`, value: p.parameter }))}
                  optionLabel="label" optionValue="value" filter filterPlaceholder="Search parameter…" resetFilterOnHide
                  placeholder={!paramCode ? 'Select an error code first' : paramKeysQ.isLoading ? 'Loading…' : paramKeys.length ? 'Select a parameter…' : 'No parameters on this code'}
                  disabled={!paramCode || !paramKeys.length}
                  onChange={(e: { value: string }) => setParamKey(e.value || '')} style={{ width: '100%' }} />
              </label>
              {paramResult && paramResult.distinctValues !== undefined && (
                <div className="ea-paramstat">
                  <div><div className="n num">{fmt(paramResult.distinctValues)}</div><div className="l">distinct values</div></div>
                  <div><div className="n num">{fmt(paramResult.totalOccurrences ?? 0)}</div><div className="l">occurrences</div></div>
                </div>
              )}
            </div>

            {!paramCode ? (
              <div className="ea-modal-empty">Select an error code to analyse its parameters.</div>
            ) : paramValuesQ.isLoading ? (
              <div className="ea-modal-empty">Loading…</div>
            ) : paramResult?.values?.length ? (
              <div className="ea-scrolllist" style={{ maxHeight: 520 }}>
                {paramResult.values.map((v, i) => (
                  <div className="ea-rank" key={v.value + i}
                    onClick={() => drillTo({ code: paramCode, param: { name: paramKey, value: v.value } })}
                    title={`Show the ${fmt(v.count)} records where ${paramKey} = ${v.value}`}>
                    <div className="rk">{String(i + 1).padStart(2, '0')}</div>
                    <div className="body">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                        <span className="code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v.value}</span>
                      </div>
                      <div className="desc">{paramKey} · {fmt(v.affectedUsers)} {v.affectedUsers === 1 ? 'user' : 'users'}{v.lastSeen ? ` · last ${timeAgo(v.lastSeen)}` : ''}</div>
                      <div className="prog"><i style={{ width: `${v.percentage}%`, background: C.indigo, animationDelay: `${i * 40}ms` }} /></div>
                    </div>
                    <div className="rt"><div className="oc num">{fmt(v.count)}<small>{v.percentage}% of {paramKey}</small></div></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ea-modal-empty">{paramKey ? 'No values recorded for this parameter in the selected range.' : 'Select a parameter to see its top values.'}</div>
            )}
          </section>
        ) : (
          /* ---------------- RECORDS TAB ---------------- */
          <section className="ea-card ea-reveal">
            <div className="ea-card-head pad" style={{ marginBottom: 0, paddingBottom: 14, borderBottom: '1px solid var(--ea-line)', flexWrap: 'wrap', gap: 10 }}>
              <div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#0EA5E9,#6366F1)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></svg></span>Error Log Stream <span className="ea-sub" style={{ fontWeight: 500 }}>· {fmt(totalRows)} records</span></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <SearchInput value={qInput} onChange={setQInput} placeholder="Search code or message…" width={220} />
                <Dropdown value={tblSev} options={[{ label: 'All Severities', value: 'all' }, { label: 'Critical', value: 'Critical' }, { label: 'High', value: 'High' }, { label: 'Medium', value: 'Medium' }, { label: 'Low', value: 'Low' }]}
                  optionLabel="label" optionValue="value" onChange={(e: { value: Sev | 'all' }) => { setTblSev(e.value); setPage(0); }} style={{ minWidth: 150 }} />
                <Dropdown value={srcType} options={[{ label: 'All Types', value: 'all' }, ...srcItems.map((s) => ({ label: s.name, value: s.name }))]}
                  optionLabel="label" optionValue="value" onChange={(e: { value: string }) => { setSrcType(e.value); setPage(0); }} style={{ minWidth: 140 }} />
                <Dropdown value={endpointFilter} options={[{ label: 'All Endpoints', value: '' }, ...endpointItems.map((e) => ({ label: e.name, value: e.name }))]}
                  optionLabel="label" optionValue="value" filter filterPlaceholder="Search endpoint…" resetFilterOnHide showClear={!!endpointFilter}
                  placeholder="All Endpoints" onChange={(e: { value: string }) => { setEndpointFilter(e.value || ''); setPage(0); }} style={{ minWidth: 210 }} />
                <Dropdown value={userFilter} options={[{ label: 'All Users', value: '' }, ...topUsers.map((u) => ({ label: u.user, value: u.userId }))]}
                  optionLabel="label" optionValue="value" filter filterPlaceholder="Search user…" resetFilterOnHide showClear={!!userFilter}
                  placeholder="All Users" onChange={(e: { value: string }) => { setUserFilter(e.value || ''); setPage(0); }} style={{ minWidth: 180 }} />
                <button className="ea-btn" style={{ height: 32 }} onClick={() => searchQ.refetch()}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v5h-5" /></svg>Refresh</button>
              </div>
            </div>

            {/* active filters — shows what a drill-through from Overview applied, each removable */}
            {activeFilters.length > 0 && (
              <div className="ea-activefilters">
                <span className="lbl">Filtered by</span>
                {activeFilters.map((f) => (
                  <button aria-label="Remove this filter" key={f.key} className="ea-fchip" onClick={f.clear} title="Remove this filter">
                    {f.label}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" width="11"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                ))}
                <button className="ea-fclear" onClick={clearAllFilters}>Clear all</button>
              </div>
            )}
            <SaasDataTable
              tableId="error-records"
              /* Dashboard layout (stat cards + charts above): grow to content and let
                 the page scroll, instead of fill-to-tiny under the cards. */
              fillHeight={false}
              columns={recordColumns}
              data={gridRows}
              loading={searchQ.isLoading}
              dataKey="_key"
              emptyMessage="No error logs match the current filters."
              first={page * perPage}
              rows={perPage}
              totalRecords={totalRows}
              onPage={(e: { first: number }) => setPage(Math.floor(e.first / perPage))}
              rowsPerPageOptions={[perPage]}
              sortField={sort.col}
              sortOrder={sort.dir}
              onSort={(e: { sortField: string }) => e.sortField && setSortCol(e.sortField)}
              filters={{}}
              onFilter={() => { /* records are filtered from the toolbar above */ }}
              testId="ErrorAnalytics.Records"
            />
          </section>
        )}
      </div>

      {/* tooltip */}
      {tip.show && <div className="ea-tt" style={{ left: Math.min(tip.x + 14, window.innerWidth - 250), top: Math.min(tip.y + 14, window.innerHeight - 150) }}>{tip.content}</div>}

      {/* Unique Errors / Affected Users drill-in list */}
      {listModal && (
        <>
          <div className="ea-scrim show" onClick={() => setListModal(null)} />
          <div className="ea-modal" role="dialog" aria-modal="true">
            <div className="ea-modal-head">
              <div>
                <div className="ea-title" style={{ fontSize: 14 }}>{listModal === 'codes' ? 'Unique Error Codes' : 'Affected Users'}</div>
                <div className="ea-sub" style={{ marginTop: 3 }}>{fmt(listRows.length)} {listModal === 'codes' ? 'codes' : 'users'} · {rangeLabel.toLowerCase()}</div>
              </div>
              <button className="ea-iconbtn" onClick={() => setListModal(null)} aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
            </div>
            <div style={{ padding: '0 18px 12px' }}>
              <Input value={listSearch} onChange={(value: string) => setListSearch(value)}
                placeholder={listModal === 'codes' ? 'Search error code or message…' : 'Search user name or id…'}
                icon="pi pi-search" style={{ width: '100%' }} />
            </div>
            <div className="ea-modal-body">
              {listModal === 'codes' && allCodesQ.isLoading && <div className="ea-modal-empty">Loading…</div>}
              {listRows.map((r, i) => (
                <button aria-label="Show these records" className="ea-modal-row" key={r.key + i} onClick={() => { r.pick(); setListModal(null); }} title="Show these records">
                  <span className="rk">{String(i + 1).padStart(2, '0')}</span>
                  <span className="body">
                    <span className="p">{r.primary}{r.sev && <SevChip s={r.sev} />}</span>
                    <span className="s">{r.secondary}</span>
                  </span>
                  <span className="ct num">{fmt(r.v)}</span>
                </button>
              ))}
              {!listRows.length && !(listModal === 'codes' && allCodesQ.isLoading) && (
                <div className="ea-modal-empty">{listSearch ? 'Nothing matches that search.' : 'No data in the selected window.'}</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* drawer */}
      <div className={`ea-scrim ${drawer?.open ? 'show' : ''}`} onClick={closeDrawer} />
      <aside className={`ea-drawer ${drawer?.open ? 'show' : ''}`} aria-label="Error detail">
        {drawer && (
          <>
            <div className="ea-dhead">
              <div className="ea-crumb" style={{ marginBottom: 12 }}>{drawer.crumb.map((s, i) => i < drawer.crumb.length - 1 ? <span key={i}>{s}<span className="sep" style={{ margin: '0 7px' }}>›</span></span> : <b key={i}>{s}</b>)}</div>
              <div className="top"><div><div className="ea-dcode">{drawer.err.code}</div><div className="ea-dttl">{drawer.err.desc}</div></div>
                <button className="ea-iconbtn" onClick={closeDrawer} aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button></div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}><SevChip s={drawer.err.sev} /><span className="ea-status open" style={{ marginLeft: 'auto' }}>Open</span></div>
              <div className="ea-dstats">
                {/* For a record these are counts for its error code — a single event has no
                    aggregates of its own. The specific user id lives in the Overview tab. */}
                {(drawer.err.isRecord
                  ? [
                      ['Occurrences', drawerCodeSummary ? fmt(drawerCodeSummary.totalErrors) : '…', 'Times this error code occurred in the selected range'],
                      ['Users', drawerCodeSummary ? fmt(drawerCodeSummary.totalAffectedUsers) : '…', 'Distinct users hit by this error code'],
                      ['Occurred at', drawer.err.last, 'When this specific event happened'],
                      ['Source', drawer.err.svc[0] || '—', 'Where this event came from'],
                    ]
                  : [
                      ['Occurrences', fmt(drawer.err.v), 'Total occurrences in the selected range'],
                      ['Users', fmt(drawer.err.users), 'Distinct users affected'],
                      ['Last seen', drawer.err.last, 'Most recent occurrence'],
                      ['Sources', String(drawer.err.svc.length), 'Distinct sources'],
                    ]
                ).map((s) => <div className="ea-dstat" key={s[0]} title={s[2]}><div className="l">{s[0]}</div><div className="v num">{s[1]}</div></div>)}
              </div>
            </div>
            <div className="ea-dbody">
              <div className="ea-tabs">{(['overview', 'stack', 'timeline', 'related'] as DrawerTab[]).map((t) => <button key={t} className={dTab === t ? 'on' : ''} onClick={() => setDTab(t)}>{t === 'stack' ? 'Stack Trace' : t[0].toUpperCase() + t.slice(1)}</button>)}</div>
              <DrawerPanel tab={dTab} err={drawer.err} stack={drawer.stack} related={codeItems} tip={tipApi} onPick={(c, l) => openCode(c, ['Overview', 'Related', l])} />
            </div>
            {!drawer.noFooter && (
              <div className="ea-dfoot">
                <button className="ea-btn primary grow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>Assign</button>
                <button className="ea-btn success grow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>Resolve</button>
                <button className="ea-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>Copy</button>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
